import { BackgroundCheckType, backgroundCheckTypes } from '@/backend/features/background-check/types';
import { AppContext } from '@/ui/Context';
import { getEnabledBackgroundCheckTypes } from '@/ui/features/background-check/utils/getEnabledBackgroundCheckTypes';
import { showFormDialog } from '@juvare/common-form-ui/showFormDialog';
import { showAlertDialog } from '@juvare/common-web-ui/DialogHelper';
import { showInfoSnackbar } from '@juvare/common-web-ui/SnackbarHelper';
import { requireAnySystemPermission } from '@juvare/contact-management/backend/features/permissions/shared';
import { getSettings } from '@juvare/platform-sdk-react/ui/features/editable-settings';
import { useFunctionClient } from '@juvare/platform-sdk-react/ui/hooks';
import { Box, Button, Stack } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

export interface Props {
    updateQuota: () => void;
}

export const BackgroundCheckToolbar: React.FC<Props> = ({ updateQuota }) => {
    const { t } = useTranslation();
    const functionClient = useFunctionClient();
    const appContext = React.useContext(AppContext);

    const { backgroundCheckConfig } = useSelector(getSettings);
    const enabledBackgroundCheckTypes = getEnabledBackgroundCheckTypes(backgroundCheckConfig.type);
    const isJasBackgroundCheckEnabled = enabledBackgroundCheckTypes.some(
        ({ value }) => backgroundCheckTypes[value as BackgroundCheckType]?.jasType,
    );

    const handleAddQuota = () => {
        showFormDialog({
            title: t('pages.backgroundCheck.backgroundCheckQuotaForm'),
            data: {
                additionalQuota: 50,
            },
            config: {
                type: 'form',
                children: [
                    {
                        type: 'form.select',
                        key: 'type',
                        label: t('pages.backgroundCheck.backgroundCheckType'),
                        valueProvider: {
                            type: 'backgroundCheckJasEnabledTypes',
                        },
                        validate: {
                            required: true,
                        },
                    },
                    {
                        type: 'form.number',
                        key: 'additionalQuota',
                        label: t('pages.backgroundCheck.quotaNumberLabel'),
                    },
                ],
            },
            onSubmit: async (data) => {
                await functionClient.executeFunction({
                    functionName: 'increaseBackgroundCheckQuota',
                    params: { additionalQuota: data.additionalQuota, type: data.type },
                });

                updateQuota();

                showInfoSnackbar({
                    message: t('pages.backgroundCheck.quotaIncrease', { count: data.additionalQuota }),
                    autoHideDuration: 2000,
                });
            },
        });
    };

    return (
        <Box
            sx={{
                width: { xs: '100%', md: 'auto' },
                display: 'flex',
                flexDirection: { xs: 'column-reverse', sm: 'row' },
                gap: 2,
            }}
        >
            {requireAnySystemPermission(appContext.userState, 'view-screenings') && (
                <Button
                    sx={{ flexGrow: 1 }}
                    variant='outlined'
                    data-test='download-button'
                    onClick={() =>
                        showAlertDialog({
                            title: t('pages.administration.downloadForms'),
                            content: (
                                <Stack alignItems='center' p={4}>
                                    <Box maxWidth={400}>
                                        <Button
                                            fullWidth
                                            sx={{ m: '15px auto' }}
                                            variant='contained'
                                            color='primary'
                                            download
                                            href='/assets/intelliCorp_report_copy_request_form.pdf'
                                            data-test='download-report-copy-form'
                                        >
                                            {t('pages.administration.reportCopyForm')}
                                        </Button>
                                        <Button
                                            fullWidth
                                            sx={{ m: '15px auto' }}
                                            variant='contained'
                                            color='primary'
                                            download
                                            href='/assets/intelliCorp_reinvestigation_request_form_2014.pdf'
                                            data-test='download-reinvestigation-form'
                                        >
                                            {t('pages.administration.reinvestigationForm')}
                                        </Button>
                                    </Box>
                                </Stack>
                            ),
                        })
                    }
                >
                    {t('pages.administration.downloadForms')}
                </Button>
            )}
            {requireAnySystemPermission(appContext.userState, 'add-quota') && isJasBackgroundCheckEnabled && (
                <Button sx={{ flexGrow: 1 }} variant='contained' onClick={handleAddQuota} data-test='add-quota-button'>
                    {t('pages.backgroundCheck.addQuota')}
                </Button>
            )}
        </Box>
    );
};
